import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography
} from '@mui/material';
import { api } from '../contexts/ApiContext';
import { FIT_TABLE_SX, FitColgroup, buildFitColgroup, fitTruncateSx } from '../utils/fitTable';

export type ProbabilisticCompareDim = {
  agree?: boolean;
  rules?: string | null;
  probabilistic?: string | null;
};

export type ProbabilisticComparePayload = {
  has_prediction?: boolean;
  agree_count?: number | null;
  disabled?: boolean;
  dims?: Record<string, ProbabilisticCompareDim>;
  rules?: {
    cashflow?: string | null;
    supplier?: string | null;
    category?: string | null;
    vat?: string | null;
  };
  probabilistic?: {
    status?: string;
    cashflow?: { label?: string; confidence?: number; rationale?: string };
    supplier?: { name?: string; confidence?: number; rationale?: string };
    category?: { name?: string; confidence?: number; rationale?: string };
    vat?: { vat_rate?: string; confidence?: number; rationale?: string };
  } | null;
  prediction_status?: string;
};

const DIM_LABELS: { key: string; label: string }[] = [
  { key: 'cashflow', label: 'Cash flow' },
  { key: 'supplier', label: 'Supplier' },
  { key: 'category', label: 'Category' },
  { key: 'vat', label: 'VAT' }
];

export function probabilisticAgreeChipColor(
  agreeCount: number | null | undefined
): 'success' | 'error' | 'warning' | 'default' {
  if (agreeCount == null) return 'default';
  if (agreeCount >= 4) return 'success';
  if (agreeCount <= 0) return 'error';
  return 'warning';
}

type Props = {
  open: boolean;
  txId: number;
  description?: string;
  compare: ProbabilisticComparePayload | null;
  onClose: () => void;
  onAccepted: () => void | Promise<void>;
};

export default function ProbabilisticRulesCompareModal({
  open,
  txId,
  description,
  compare,
  onClose,
  onAccepted
}: Props) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [overwrite, setOverwrite] = useState(false);

  const agreeCount = compare?.agree_count;
  const dims = compare?.dims || {};
  const isPlaceholderProbLabel = (raw: unknown): boolean => {
    const t = String(raw ?? '')
      .trim()
      .toLowerCase();
    return (
      !t ||
      t === '—' ||
      t === '-' ||
      t === 'n/a' ||
      t === 'na' ||
      t === 'none' ||
      t === 'unknown' ||
      t === 'unknown supplier' ||
      t === 'unknown category'
    );
  };
  const actionableDims = (['cashflow', 'supplier', 'category', 'vat'] as const).filter((key) => {
    const label = dims[key]?.probabilistic;
    if (key === 'cashflow') return !isPlaceholderProbLabel(label);
    const conf = Number((compare?.probabilistic as any)?.[key]?.confidence);
    if (Number.isFinite(conf) && conf < 0.2) return false;
    return !isPlaceholderProbLabel(label);
  });
  const canAccept = Boolean(compare?.has_prediction && actionableDims.length > 0);

  const accept = async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await api.post(`/api/probabilistic-tx/predictions/${txId}/accept`, {
        dims: actionableDims.length ? [...actionableDims] : ['cashflow'],
        overwrite_user_specified: overwrite
      });
      const blocked = res.data?.blocked_user_specified || [];
      if (blocked.length && !overwrite) {
        setError(
          `Blocked user-specified dims: ${blocked.join(', ')}. Enable overwrite and Accept again, or Accept only free dims.`
        );
        setOverwrite(true);
        return;
      }
      await onAccepted();
      onClose();
    } catch (e: any) {
      setError(String(e?.response?.data?.error || e?.message || 'Accept failed'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Rules vs Probabilistic — TX {txId}
        {agreeCount != null ? (
          <Chip
            size="small"
            sx={{ ml: 1 }}
            color={probabilisticAgreeChipColor(agreeCount)}
            label={`${agreeCount}/4 agree`}
          />
        ) : null}
      </DialogTitle>
      <DialogContent dividers>
        {description ? (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            {description}
          </Typography>
        ) : null}
        {error ? (
          <Alert severity="warning" sx={{ mb: 1.5 }}>
            {error}
          </Alert>
        ) : null}
        {!compare?.has_prediction ? (
          <Alert severity="info">No probabilistic prediction for this transaction yet.</Alert>
        ) : !canAccept ? (
          <Alert severity="warning" sx={{ mb: 1.5 }}>
            Prediction has insufficient evidence for supplier/category/VAT (sparse ledger fields). Cash
            flow may still be inferred from amount sign. Accept is disabled until a stronger score exists.
          </Alert>
        ) : null}
        {compare?.has_prediction ? (
          <Table size="small" sx={FIT_TABLE_SX}>
            <FitColgroup
              cols={buildFitColgroup([
                { key: 'dimension', pct: 18 },
                { key: 'rules', pct: 28 },
                { key: 'probabilistic' },
                { key: 'match', pct: 12 },
              ])}
            />
            <TableHead>
              <TableRow>
                <TableCell>Dimension</TableCell>
                <TableCell>Rules (current tags)</TableCell>
                <TableCell>Probabilistic</TableCell>
                <TableCell align="center">Match</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {DIM_LABELS.map(({ key, label }) => {
                const d = dims[key] || {};
                const pBlob = (compare.probabilistic as any)?.[key] || {};
                const conf =
                  typeof pBlob.confidence === 'number' ? ` (${Math.round(pBlob.confidence * 100)}%)` : '';
                return (
                  <TableRow key={key}>
                    <TableCell>{label}</TableCell>
                    <TableCell>
                      <Box sx={fitTruncateSx} title={d.rules || ''}>
                        {d.rules || '—'}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box>
                        {(d.probabilistic || '—') + conf}
                        {pBlob.rationale ? (
                          <Typography variant="caption" display="block" color="text.secondary">
                            {pBlob.rationale}
                          </Typography>
                        ) : null}
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        size="small"
                        label={d.agree ? 'Yes' : 'No'}
                        color={d.agree ? 'success' : 'default'}
                        variant={d.agree ? 'filled' : 'outlined'}
                      />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        ) : null}
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1.5 }}>
          Accept writes probabilistic values into the real tag tables (same path as manual tags). Confirmed /
          user-specified tags are not overwritten unless you confirm overwrite.
        </Typography>
      </DialogContent>
      <DialogActions>
        <Stack direction="row" spacing={1} sx={{ width: '100%', justifyContent: 'flex-end', px: 1, pb: 0.5 }}>
          <Button onClick={onClose} disabled={busy}>
            Close
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={() => void accept()}
            disabled={busy || !canAccept}
            startIcon={busy ? <CircularProgress size={14} color="inherit" /> : undefined}
          >
            {overwrite ? 'Accept (overwrite user-specified)' : 'Accept probabilistic tags'}
          </Button>
        </Stack>
      </DialogActions>
    </Dialog>
  );
}
